import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  ChooseOrganization,
  Unauthenticated,
  Unreachable,
  api,
  type Me,
  type OrganizationChoice,
} from '../api/client';
import { assuranceLevel, chooseOrganization, signOut } from './session';
import { EntryLayout } from '../screens/EntryLayout';

/**
 * The gate every signed-in route passes through.
 *
 * **It asks the API, not the token.** A token that parses is not a session
 * that works: the subject may have been deactivated, their membership revoked,
 * or their session ended by an administrator. `/api/me` is the only thing that
 * knows, and it is one request the shell was already making to draw its rail.
 *
 * There is deliberately no "is there a token" pre-check. An earlier version
 * asked local storage first and short-circuited on an empty answer, which made
 * the browser the authority on whether a session existed and gave the same
 * result two ways — one of which could be wrong. Now the request is made, the
 * client attaches whatever credential it has, and a 401 is the answer.
 *
 * **A refusal is not an error.** Four things can come back and only one is a
 * failure: sign in again, choose an organization, wait for the service, or a
 * real problem. Collapsing them into "something went wrong" is how a person
 * with two organizations gets stuck on a page that will never load — and how a
 * thirty-second Render cold start becomes an invitation to sign out of a
 * perfectly good session.
 *
 * **The destination is preserved.** Being returned to the portfolio after
 * signing in again is how somebody loses the thing they were reading.
 */

export type Identity = { me: Me };

type State =
  | { kind: 'checking' }
  | { kind: 'signed-out' }
  | { kind: 'choose'; organizations: OrganizationChoice[] }
  | { kind: 'unreachable'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; me: Me };

export function useIdentity(): [State, () => void] {
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (!cancelled) setState({ kind: 'ready', me });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof Unauthenticated) setState({ kind: 'signed-out' });
        else if (e instanceof ChooseOrganization)
          setState({ kind: 'choose', organizations: e.organizations });
        else if (e instanceof Unreachable)
          setState({ kind: 'unreachable', message: e.message });
        else setState({ kind: 'error', message: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  // Stable across renders. An inline arrow is a new function every time, and
  // anything that depends on it in a `useEffect` — the picker answering its
  // own single-choice question, for one — re-runs forever.
  const recheck = useCallback(() => setNonce((n) => n + 1), []);
  return [state, recheck] as const;
}

/**
 * The picker for a subject who belongs to more than one organization.
 *
 * The API refuses rather than guessing, and this is why: a property manager
 * who works for two firms would otherwise see one firm's buildings under the
 * other's name, and nothing on the screen would say which. Choosing is a
 * deliberate act and it is stored, so it is asked once rather than every time.
 */
/**
 * Which organization is this session acting in?
 *
 * **One membership answers itself.** The server refuses to assume a default —
 * an implicit "take the first row" changes behaviour silently the day a second
 * membership appears, so it asks. But being asked and having a choice are
 * different things: when the server offers exactly one, the client names it
 * and the server still verifies that name against the membership set. Nothing
 * is assumed anywhere; a question with one answer is simply answered.
 *
 * That is the whole of it for almost every customer. A property manager with
 * one firm should never meet this screen, and the first version showed them a
 * UUID and a paragraph about why it could not do better.
 *
 * **Names, not identifiers.** Migration 0018 makes an organization's name
 * readable in the phase before one is chosen. An identifier is not an answer
 * somebody can give.
 */
export function OrganizationPicker({
  organizations,
  onChosen,
}: {
  organizations: OrganizationChoice[];
  onChosen: () => void;
}) {
  const only = organizations.length === 1 ? organizations[0] : undefined;

  useEffect(() => {
    if (!only) return;
    chooseOrganization(only.id);
    onChosen();
  }, [only, onChosen]);

  if (only)
    return (
      <EntryLayout>
        <h1>Signing you in</h1>
        <p aria-live="polite">One moment.</p>
      </EntryLayout>
    );

  return (
    <EntryLayout>
      <h1>Choose an organization</h1>
      <p>
        Your account works with more than one. Pick the one you want to work in
        — you can switch later.
      </p>
      <ul className="record-list org-choices">
        {organizations.map((choice) => (
          <li key={choice.id}>
            <button
              type="button"
              onClick={() => {
                chooseOrganization(choice.id);
                onChosen();
              }}
            >
              {/* A name where there is one. Where there is not, the identifier
                  is still a choice somebody can make — worse than a name, and
                  far better than an empty list. */}
              {choice.name ?? choice.id}
            </button>
          </li>
        ))}
      </ul>
    </EntryLayout>
  );
}

/** Wraps the routes that need a session. */
export function RequireSession({
  children,
}: {
  children: (identity: Identity) => React.ReactNode;
}) {
  const location = useLocation();
  const [state, recheck] = useIdentity();

  if (state.kind === 'checking')
    return (
      <p aria-live="polite" className="entry">
        Checking your session…
      </p>
    );

  if (state.kind === 'signed-out') {
    // `next` carries the destination through the round trip.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (state.kind === 'choose')
    return (
      <OrganizationPicker organizations={state.organizations} onChosen={recheck} />
    );

  if (state.kind === 'unreachable')
    return (
      <EntryLayout>
        <h1>Premise Sense is not responding</h1>
        <p role="alert">{state.message}</p>
        <p className="basis">
          Your session is fine. The service may be starting up, which takes
          about half a minute on the current plan — this page does not sign you
          out, because a wake-up is not an outage.
        </p>
        {/* Retry, not sign out. Offering the destructive one first is how
            somebody loses a working session to a cold start. */}
        <button type="button" onClick={recheck}>
          Try again
        </button>
      </EntryLayout>
    );

  if (state.kind === 'error') {
    // The API answers every refusal with a bare `forbidden` — deliberately, so
    // a caller cannot enumerate why. That is right for the wire and useless on
    // a screen: "HTTP 403" with a correlation id is a dead end for the person
    // holding the account.
    //
    // What this side legitimately knows is its own token. Owner and Admin are
    // refused without a second factor, and if this session has not presented
    // one that is by far the likeliest reason — so it is offered as a next
    // step rather than asserted as the cause. Anything else, and the
    // correlation id is what support joins on.
    const unassured = assuranceLevel() === 'aal1';
    return (
      <EntryLayout>
        <h1>Something went wrong</h1>
        <p role="alert">{state.message}</p>
        {unassured && (
          <p className="basis">
            This session has not presented a second factor, and Owner and Admin
            accounts are refused without one. If that is the reason,{' '}
            <Link to="/mfa">set it up or present it here</Link>.
          </p>
        )}
        <p className="basis">
          If an organization was being remembered from a previous session, it
          has been forgotten — <button type="button" className="linklike"
            onClick={recheck}>try again</button> and you will be asked which one
          to use.
        </p>
        <p className="basis basis--unknown">
          The product is not told which check refused, on purpose. Quote{' '}
          {state.message.match(/ref ([0-9a-f-]+)/)?.[1] ?? 'the reference above'}{' '}
          and the reason can be found in the log.
        </p>
        <button
          type="button"
          onClick={() => {
            signOut();
            recheck();
          }}
        >
          Sign out and start again
        </button>
      </EntryLayout>
    );
  }

  return <>{children({ me: state.me })}</>;
}
